import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { PlatformLayout } from "./layouts/PlatformLayout";
import {
  AccessDeniedPage,
  LoginPage,
  PlatformAccessGuard,
} from "../modules/auth";
import { PlatformDashboardPage } from "../modules/dashboard";
import { OrgListPage, OrgDetailPage } from "../modules/organizations";
import { UsersListPage, UserDetailPage } from "../modules/users";
import {
  ModerationAppealDetailPage,
  ModerationCenterPage,
  ModerationReportDetailPage,
} from "../modules/moderation";
import { AuditLogPage } from "../modules/audit";
import { OnboardingPipelinePage } from "../modules/onboarding";
import { PlatformSettingsPage } from "../modules/settings";
import {
  ContractTermsFormPage,
  DefaultContractTermsPage,
  VersionHistoryPage,
  AcceptanceTimelinePage,
  FeePolicyDraftIndexPage,
  FeePolicyDraftEditorPage,
  FeePolicyVersionPage,
  FeePolicyPublicationPage,
  ModuleOfferDraftIndexPage,
  ModuleOfferDraftEditorPage,
  ModuleOfferPublicationPage,
  ModuleOfferVersionPage,
} from "../modules/contracts";
import { ApprovalsPage } from "../modules/approvals";
import { AlertsPage } from "../modules/alerts";
import { FinancePage } from "../modules/finance";
import { FacilitiesListPage, PlaceReviewPage, VenueDrilldownPage } from "../modules/facilities";
import { AnnouncementsPage } from "../modules/announcements";
import { CompliancePage, DoNotSellPage } from "../modules/compliance";
import { ReservationOperationsPage } from "../modules/reservations";
import { CoachExperienceOperationsPage } from "../modules/coach-experience";
import { SanctionRequestsPage, SanctionedEventsPage } from "../modules/sanctioning";

export function Router() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Auth */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/access-denied" element={<AccessDeniedPage />} />

        {/* Legal — public, no auth required */}
        <Route path="/do-not-sell" element={<DoNotSellPage />} />

        {/* All routes require an internal employee email plus platform_admin claim */}
        <Route element={<PlatformAccessGuard />}>
          {/* Main platform shell */}
          <Route element={<PlatformLayout />}>
            <Route index element={<PlatformDashboardPage />} />

            {/* Organizations */}
            <Route path="organizations" element={<OrgListPage />} />
            <Route path="organizations/:id" element={<OrgDetailPage />} />

            {/* Contract terms (per-org) */}
            <Route
              path="organizations/:orgId/contract-terms"
              element={<ContractTermsFormPage />}
            />
            <Route
              path="organizations/:orgId/contract-versions"
              element={<VersionHistoryPage />}
            />

            {/* Acceptance timeline */}
            <Route path="contracts/module-offers" element={<ModuleOfferDraftIndexPage />} />
            <Route path="contracts/module-offers/new" element={<ModuleOfferDraftEditorPage />} />
            <Route path="contracts/module-offers/:draftId" element={<ModuleOfferDraftEditorPage />} />
            <Route path="contracts/module-offers/:draftId/publication" element={<ModuleOfferPublicationPage />} />
            <Route path="contracts/module-offers/versions/:versionId" element={<ModuleOfferVersionPage />} />
            <Route path="contracts/default-terms" element={<DefaultContractTermsPage />} />
            <Route path="contracts/fee-policies" element={<FeePolicyDraftIndexPage />} />
            <Route path="contracts/fee-policies/new" element={<FeePolicyDraftEditorPage />} />
            <Route path="contracts/fee-policies/:draftId" element={<FeePolicyDraftEditorPage />} />
            <Route path="contracts/fee-policies/:draftId/publication" element={<FeePolicyPublicationPage />} />
            <Route path="contracts/fee-policies/versions/:versionId" element={<FeePolicyVersionPage />} />
            <Route
              path="onboarding/acceptance"
              element={<AcceptanceTimelinePage />}
            />

            {/* Approvals pipeline */}
            <Route path="approvals" element={<ApprovalsPage />} />

            {/* Facilities */}
            <Route path="facilities" element={<FacilitiesListPage />} />
            <Route path="facilities/places" element={<PlaceReviewPage />} />
            <Route path="facilities/:id" element={<VenueDrilldownPage />} />

            {/* Tournament sanctioning */}
            <Route path="sanctioning" element={<SanctionRequestsPage />} />
            <Route path="sanctioning/events" element={<SanctionedEventsPage />} />

            {/* Cross-tenant reservation operations */}
            <Route
              path="reservations"
              element={<ReservationOperationsPage />}
            />

            {/* Users */}
            <Route path="users" element={<UsersListPage />} />
            <Route path="users/:id" element={<UserDetailPage />} />

            {/* Moderation */}
            <Route path="moderation" element={<ModerationCenterPage />} />
            <Route
              path="moderation/appeals"
              element={<ModerationCenterPage />}
            />
            <Route path="moderation/bans" element={<ModerationCenterPage />} />
            <Route
              path="moderation/reports/:reportId"
              element={<ModerationReportDetailPage />}
            />
            <Route
              path="moderation/appeals/:appealId"
              element={<ModerationAppealDetailPage />}
            />

            {/* Alerts */}
            <Route path="alerts" element={<AlertsPage />} />

            {/* Finance */}
            <Route path="finance" element={<FinancePage />} />

            {/* Audit */}
            <Route path="audit" element={<AuditLogPage />} />

            {/* Compliance (CCPA deletion activity) */}
            <Route path="compliance" element={<CompliancePage />} />
            <Route path="coach-experience" element={<CoachExperienceOperationsPage />} />

            {/* Announcements */}
            <Route path="announcements" element={<AnnouncementsPage />} />

            {/* Onboarding */}
            <Route path="onboarding" element={<OnboardingPipelinePage />} />

            {/* Settings */}
            <Route path="settings" element={<PlatformSettingsPage />} />

            {/* 404 catch-all */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
        </Route>
      </Routes>
    </BrowserRouter>
  );
}
